/* eslint-disable complexity */
import { Countries, FormattedMessage, WrappedComponentProps, injectIntl } from '@futrli/localisation';
import { PREDICT_PRODUCT_SLUG, ProductSlug } from '@futrli/shared-services';
import classNames from 'classnames';
import { capitalize, find, get, invoke, pick } from 'lodash';
import React from 'react';
import { LoaderSpinner } from 'carbon-react/lib/components/loader-spinner';
import { injectStripe, ReactStripeElements } from 'react-stripe-elements';

import { trackPaymentError } from '../../../core/tracker';
import { IntentActions } from '../../../core/types';
import { textFieldValidator } from '../../../utils/form-validator-utils';
import { handlePaymentIntent, handleSetupIntent } from '../../../utils/stripe-utils';
import { CARD_PAYMENT_METHOD, EMPTY_FORM_DATA } from '../../constants';
import { FormConfig, FormField, FormFieldKeys, PaymentFormData } from '../../types';
import { PaymentFormFields } from './payment-form-fields';
import { LDProps } from '../../../../../skeleton/src/core/providers/launch-darkly';
import styles from './payment-form.scss';

export interface UISearchSelectOption {
  label: string;
  value: string | number;
}

interface State {
  form: Partial<PaymentFormData>;
  error: stripe.PaymentMethodResponse['error'] | null;
  paymentMethodComplete: boolean;
  countries: UISearchSelectOption[];
}

export interface PaymentFormProps {
  isSubmitting?: boolean;
  submittingComponent?: JSX.Element;
  submittingMessage?: JSX.Element | string;
  onSubmit?: (formData: PaymentFormData) => void;
  onPreSubmit?: (formData: PaymentFormData) => Promise<IntentActions | undefined>;
  onStripeError?: (error?: stripe.Error) => void;
  externalError?: JSX.Element | null;
  formActions?: JSX.Element;
  formAdditionalFields?: JSX.Element;
  formConfig: FormConfig;
  formData?: Partial<PaymentFormData>;
  onValidate?: (valid: boolean) => void;
  product?: ProductSlug;
  workspaceId?: string;
  usePaymentIntent?: boolean;
  isSubscribeAndPurchaseDialog?: boolean;
  setIsSubmitting: (isSubmitting: boolean) => void;
}

type Props = PaymentFormProps & ReactStripeElements.InjectedStripeProps & WrappedComponentProps & LDProps;

export class PaymentFormComponent extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      form: props.formData || pick(EMPTY_FORM_DATA, props.formConfig.fields),
      countries: [],
      error: null,
      paymentMethodComplete: false
    };
  }

  public componentDidMount(): void {
    this.getCountries();
  }

  public render() {
    const { formActions, formAdditionalFields, isSubmitting, stripe, externalError } = this.props;
    const { countries, form, error } = this.state;

    if (!stripe) {
      return <LoaderSpinner />;
    }

    const showNewForm = !this.props.isSubscribeAndPurchaseDialog;

    return (
      <PaymentFormFields
        showRenderField={this.showRenderField}
        handleSubmit={this.handleSubmit}
        form={form}
        error={error}
        externalError={externalError}
        isDisabled={this.isDisabled}
        handleOnChange={this.handleOnChange}
        formActions={formActions}
        formAdditionalFields={formAdditionalFields}
        isSubmitting={isSubmitting}
        getOnSubmittingElement={this.getOnSubmittingElement}
        getTextFieldErrorMessage={this.getTextFieldErrorMessage}
        isValidField={this.isValidField}
        countries={countries}
        handleOnSearchFieldChange={this.handleOnSearchFieldChange}
        handleStateProvinceChange={this.handleStateProvinceChange}
        getCountryOptionFromCode={this.getCountryOptionFromCode}
        handleStripeElementChange={this.handleStripeElementChange}
        intl={this.props.intl}
        showNewForm={showNewForm}
      />
    );
  }

  /**
   * Handles UITextField on change event.
   * @param {React.ChangeEvent<HTMLInputElement>} event
   */
  private handleOnChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    event.stopPropagation();
    const fieldName = event.target.name as FormFieldKeys;
    const fieldValue = fieldName === FormField.notVatRegistered ? event.target.checked : event.target.value;

    this.setState(
      (prevState: State) => ({
        form: {
          ...prevState.form,
          [fieldName]: fieldValue
        }
      }),
      this.validateForm
    );
  };

  /**
   * Handles UISearchSelect on change event.
   * @param {UISearchSelectOption|null} option
   */
  private handleOnSearchFieldChange = (option: UISearchSelectOption | null): void => {
    this.setState(
      (prevState: State) => ({
        form: {
          ...prevState.form,
          country: option?.value as string,
          state: undefined
        }
      }),
      this.validateForm
    );
  };

  /**
   * Handles UISearchSelect on change event.
   * @param {UISearchSelectOption|null} option
   */
  private handleStateProvinceChange = (option: UISearchSelectOption | null): void => {
    this.setState(
      (prevState: State) => ({
        form: {
          ...prevState.form,
          state: option || undefined
        }
      }),
      this.validateForm
    );
  };

  /**
   * Handles Stripe element on change event.
   * @param {ReactStripeElements.ElementChangeResponse} event
   */
  private handleStripeElementChange = (event: ReactStripeElements.ElementChangeResponse): void => {
    this.setState(
      {
        paymentMethodComplete: event.complete,
        error: null
      },
      this.validateForm
    );
  };

  /**
   * Checks if a field is disabled from props.
   * @param {FormFieldKeys} fieldName
   * @returns {boolean}
   */
  private isDisabled = (fieldName: FormFieldKeys) => {
    const { formConfig } = this.props;

    return formConfig.disabled.includes(fieldName);
  };

  /**
   * Checks if a field is disabled from props.
   * @param {FormFieldKeys} fieldName
   * @returns {boolean}
   */
  private showRenderField = (fieldName: FormFieldKeys) => {
    const { formConfig } = this.props;

    return formConfig.fields.includes(fieldName);
  };

  /**
   * Checks if a field is required from the form config.
   * @param {FormFieldKeys} fieldName
   * @returns {boolean}
   */
  private isRequired = (fieldName: FormFieldKeys) => {
    const { formConfig } = this.props;

    return formConfig && formConfig.required.includes(fieldName);
  };

  /**
   * Get the list of countries and formats it to match with search field form.
   * @returns {UISearchSelectOption[]}
   */
  private getCountries = (): void => {
    const countries = Countries.getNames(this.props.intl);
    const formattedCountries = Object.keys(countries).map(countryCode => ({
      label: countries[countryCode],
      value: countryCode
    }));

    this.setState({ countries: formattedCountries });
  };

  /**
   * Get the country option from the country code.
   * @returns {UISearchSelectOption|undefined}
   */
  private getCountryOptionFromCode = (): UISearchSelectOption | undefined => {
    const {
      countries,
      form: { country }
    } = this.state;

    return find(countries, { value: country });
  };

  /**
   * Checks if a text field is valid or not (empty field is considered
   * as valid at this level of validation)
   * @param {FormFieldKeys} - fieldName
   * @returns {boolean}
   */
  private isValidField = (fieldName: FormFieldKeys): boolean => {
    const { form } = this.state;
    const field = form[fieldName];

    if (fieldName === FormField.notVatRegistered) {
      return true;
    }

    // eslint-disable-next-line @typescript-eslint/no-base-to-string
    return !field || textFieldValidator(String(field));
  };

  /**
   * Gets the error string to show as helper text in UITextField.
   * @param {FormFieldKeys} - fieldName
   * @returns {string|undefined}
   */
  private getTextFieldErrorMessage = (fieldName: FormFieldKeys): string | undefined => {
    if (this.isValidField(fieldName)) {
      return;
    }

    return this.props.intl.formatMessage({
      id: 'validation.we_only_allow_up_to_100_characters',
      defaultMessage: 'Oops, we only allow up to 100 characters.'
    });
  };

  /**
   * Gets the component to render when form is being submitted.
   * @returns {JSX.Element}
   */
  private getOnSubmittingElement = (): JSX.Element => {
    const { submittingComponent, submittingMessage, usePaymentIntent } = this.props;

    return (
      submittingComponent || (
        <div className={styles.submittingForm}>
          <div className={styles.submittingFormWrapper}>
            <p className={classNames('uiTypeHeader', styles.message)}>
              {submittingMessage || (
                <React.Fragment>
                  {usePaymentIntent ? (
                    <FormattedMessage
                      id="billing.we_are_processing_your_payment"
                      defaultMessage="We are processing your payment"
                    />
                  ) : (
                    <FormattedMessage
                      id="billing.we_are_saving_your_details"
                      defaultMessage="We are saving your details"
                    />
                  )}
                </React.Fragment>
              )}
            </p>
            <LoaderSpinner size="large" showSpinnerLabel={false} />
          </div>
        </div>
      )
    );
  };

  /**
   * Validates the form.
   */
  private validateForm = (): void => {
    const { formConfig } = this.props;
    const { form, paymentMethodComplete } = this.state;

    let invalidForm = formConfig.fields.some((fieldKey: FormFieldKeys) => {
      const fieldValue = form[fieldKey];

      // Stripe fields are validated by stripe library.
      if (fieldKey === FormField.paymentMethodId) {
        return false;
      }

      // This field doesn't require validation
      if (fieldKey === FormField.notVatRegistered) {
        return false;
      }

      // If country is Canada or US and `state` field is not defined, form is invalid
      if (fieldKey === FormField.state) {
        if (form.country !== 'CA' && form.country !== 'US') {
          return false;
        }
        if (!form.state) {
          return true;
        }
      }

      // eslint-disable-next-line @typescript-eslint/no-base-to-string
      return this.isRequired(fieldKey) && (!fieldValue || !textFieldValidator(String(fieldValue)));
    });

    // We need to check if the form includes the payment field or not to validate
    if (this.showRenderField(FormField.paymentMethodId)) {
      invalidForm = invalidForm || !paymentMethodComplete;
    }

    // If not vat registered is not selected and vat field is empty -> form is invalid
    // If not vat registered is selected and vat field is empty -> form is valid
    if (!form[FormField.notVatRegistered] && !form[FormField.vat]) {
      invalidForm = true;
    }

    invoke(this.props, 'onValidate', !invalidForm);
  };

  /**
   * Handles the submit event getting the form data and creating the payment method
   * calling the API.
   * @param {React.FormEvent} event
   */
  private handleSubmit = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();

    // If the card field is not rendered we do not need to call stripe.
    if (!this.showRenderField(FormField.paymentMethodId)) {
      return this.setState({ error: null }, () => {
        invoke(this.props, 'onSubmit', this.state.form);
      });
    }

    return this.handleSubmitStripeScaRegulation();
  };

  /**
   * Handles the payment with the new SCA regulation functionality.
   */
  private handleSubmitStripeScaRegulation = async (): Promise<void> => {
    const {
      product = PREDICT_PRODUCT_SLUG,
      stripe,
      usePaymentIntent,
      onStripeError,
      onPreSubmit,
      setIsSubmitting
    } = this.props;
    const { form } = this.state;
    let paymentMethodId = '';

    if (!stripe) {
      return;
    }

    this.setState({ error: null });

    setIsSubmitting(true);

    const billingDetails: stripe.BillingDetails = {
      address: {
        country: form.country,
        line1: form.addressLine1,
        line2: form.addressLine2,
        postal_code: form.postalCode,
        city: form.city
      },
      name: form.paymentMethodHolderName
    };

    if (usePaymentIntent) {
      const paymentMethodResponse = await stripe.createPaymentMethod(CARD_PAYMENT_METHOD, {
        billing_details: billingDetails
      });

      if (paymentMethodResponse.error) {
        setIsSubmitting(false);
        this.setState({ error: paymentMethodResponse.error }, onStripeError);
        return;
      }

      paymentMethodId = get(paymentMethodResponse, 'paymentMethod.id', '');
    }

    const intentActions = onPreSubmit && (await onPreSubmit(form as PaymentFormData));

    if (!intentActions) {
      setIsSubmitting(false);
      return;
    }

    const response = usePaymentIntent
      ? await handlePaymentIntent(intentActions, stripe, paymentMethodId)
      : await handleSetupIntent(intentActions, stripe, billingDetails);

    if (typeof response !== 'string') {
      if (!response) {
        setIsSubmitting(false);
        return;
      }

      trackPaymentError(capitalize(product as string), response, !usePaymentIntent);
      setIsSubmitting(false);
      return this.setState({ error: response }, onStripeError);
    }

    this.setState(
      (prevState: State) => ({
        form: {
          ...prevState.form,
          paymentMethodId: response,
          paymentMethodType: CARD_PAYMENT_METHOD
        }
      }),
      () => {
        invoke(this.props, 'onSubmit', this.state.form);
      }
    );
  };
}

export const PaymentFormIntl = injectIntl(PaymentFormComponent);
export const PaymentForm = injectStripe<PaymentFormProps>(PaymentFormIntl);
